import Experience from "@folio-2026/experience"
import { blendBrightenAlpha, lerp } from "@folio-2026/shared"
import { InteractionManager } from "three.interactive"
import BloomNode from "three/examples/jsm/tsl/display/BloomNode.js"
import { color, float, pass, uniform, uv, vec2, vec4 } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import SmallButton from "./SmallButton"
import Text, { type MSDFFont } from "./Text"
import WatercolorBrush from "./WatercolorBrush"

/**
 * Creates a Scene, a Camera, the Texts and the PostProcessing.
 * @param name - The name of the section main section (e.g. "loader", "about", "contact").
 * It is possible to call createTexts(name) passing a custom selector for the leaf pages.
 * @returns A TextScene instance, containing the .
 */
export default class TextScene {
  experience: Experience
  sizes: Experience["sizes"]
  renderer: Experience["renderer"]["instance"]
  globalResources: Experience["globalResources"]
  scene!: THREE.Scene
  camera!: THREE.PerspectiveCamera
  pointer: Experience["pointer"]
  debugFolder?: FolderApi

  texts: Text[] = []
  smallButtons: SmallButton[] = []
  watercolorBrush!: WatercolorBrush

  postProcessing!: THREE.PostProcessing
  bloomPass!: BloomNode

  perlin: THREE.Texture

  interactionManager!: InteractionManager

  config: TextSceneConfig

  name: string

  fonts: {
    default: {
      atlas: THREE.Texture
      font: MSDFFont
    }
    bold: {
      atlas: THREE.Texture
      font: MSDFFont
    }
  }

  settings = {
    bloomStrength: 0.13,
    bloomRadius: 0.05,
    bloomThreshold: 0,
  }

  private distanceFromCamera = 0.2
  private currentMouseCoords = { x: 0, y: 0 }
  private lastWatercolorMouseCoords = { x: Number.NaN, y: Number.NaN }
  private lastWatercolorMovementAt = Number.NaN
  private watercolorStopDelayMs = 1000

  constructor(
    name: string,
    options: {
      perlinTexture: THREE.Texture
      debugFolder?: FolderApi
    },
  ) {
    this.name = name

    this.experience = Experience.getInstance()
    this.globalResources = this.experience.globalResources
    this.sizes = this.experience.sizes
    this.pointer = this.experience.pointer
    this.renderer = this.experience.renderer.instance

    this.debugFolder = options.debugFolder

    this.perlin = options.perlinTexture

    this.config = {
      color: uniform(color("#D9D9D9")),
      mouseCoords: uniform(vec2(0)),

      emissiveIntensity: uniform(0.01),
    }

    this.fonts = {
      default: {
        atlas: this.globalResources.atlasUbuntuSansMonoRegular as THREE.Texture,
        font: this.globalResources.bmfontUbuntuMonoRegular as MSDFFont,
      },
      bold: {
        atlas: this.globalResources.atlasUbuntuMonoBold as THREE.Texture,
        font: this.globalResources.bmfontUbuntuMonoBold as MSDFFont,
      },
    }

    this.createScene()
    this.createCamera()
    this.createInteractionManager()

    this.createTexts()

    this.createSmallButtons()

    this.createWatercolorBrush()

    this.setPostProcessing()
  }

  createInteractionManager() {
    this.interactionManager = new InteractionManager(
      this.renderer,
      this.camera,
      this.renderer.domElement,
    )
  }

  createSmallButtons(sectionName?: string) {
    const name = sectionName || this.name

    if (!name) {
      return
    }

    const buttons = document.querySelectorAll(
      `[data-name="${name}"] [data-animation="webgl-small-button"]`,
    )

    if (!buttons?.length) {
      return
    }

    const newButtons = Array.from(buttons).map((el) => {
      const shouldUpdate = !el.hasAttribute("data-animation-prevent-update")
      return new SmallButton({
        name,
        element: el as HTMLElement,
        scene: this.scene,
        interactionManager: this.interactionManager,
        perlinTexture: this.perlin,
        shouldUpdate,
      })
    })
    this.smallButtons.push(...newButtons)
  }

  createTexts(sectionName?: string) {
    const name = sectionName || this.name

    if (!name) {
      return
    }

    const texts = document.querySelectorAll(
      `[data-name="${name}"] [data-animation="webgl-text"]`,
    )

    if (!texts) {
      return
    }

    this.texts.push(
      ...Array.from(texts).map((el) => {
        const shouldUpdate = !el.hasAttribute("data-animation-prevent-update")

        const font = el.hasAttribute("data-font-bold")
          ? this.fonts.bold
          : this.fonts.default

        const textInstance = new Text({
          name,
          element: el as HTMLElement,
          scene: this.scene,
          atlas: font.atlas,
          font: font.font,
          shouldUpdate,
          interactionManager: this.interactionManager,
          debugFolder: this.debugFolder,
          uniforms: {
            ...this.config,
            mouseCoords: this.config.mouseCoords,
            color: this.config.color,
            scale: 1,
            emissiveIntensity: this.config.emissiveIntensity,
          },
        })

        return textInstance
      }),
    )
  }

  createWatercolorBrush() {
    this.watercolorBrush = new WatercolorBrush({
      textScene: this.scene,
      fbmTexture: this.perlin,
      debugFolder: this.debugFolder,
      renderScale: 0.5,
    })
  }

  createScene() {
    this.scene = new THREE.Scene()
  }

  createCamera() {
    const { width, height } = this.sizes
    this.camera = new THREE.PerspectiveCamera(
      this.distanceFromCamera,
      width / height,
    )
    this.camera.position.set(0, 0, this.distanceFromCamera)
    this.camera.lookAt(new THREE.Vector3(0, 0, 0))

    this.syncDimensions()

    this.camera.updateProjectionMatrix()

    this.scene.add(this.camera)
  }

  private syncDimensions() {
    const { height } = this.sizes

    this.camera.fov =
      2 * Math.atan(height / 2 / this.distanceFromCamera) * (180 / Math.PI)
  }

  setPostProcessing(): void {
    this.postProcessing = new THREE.PostProcessing(this.renderer)

    const scenePass = pass(this.scene, this.camera)
    const sceneTexture = scenePass.getTextureNode()
    const displacementEnabled = float(this.getIsFinePointerDevice() ? 1 : 0)

    // Render targets are sampled flipped on Y in this WebGPU pipeline.
    // We used to "double flip" by doing an extra full-screen pass into `fbo`.
    // Now we sample the ping-pong target directly and flip Y explicitly.
    const screenUv = uv()
    const watercolorUv = vec2(screenUv.x, screenUv.y.oneMinus())
    const watercolorTexture = this.watercolorBrush.diffuse.sample(watercolorUv)

    // Subtle UV displacement driven by watercolor.
    // Convert pixel displacement to UV using the brush's screen-resolution uniform.
    const invResolution = vec2(
      float(1).div(this.watercolorBrush.uniforms.resolution.x),
      float(1).div(this.watercolorBrush.uniforms.resolution.y),
    )
    const watercolorBrushValue = watercolorTexture.r.max(0).min(1)

    const pointerUvNdc01 = this.config.mouseCoords.mul(0.5).add(0.5)
    // `uv()` in this postprocessing pass is Y-flipped vs NDC->UV mapping.
    // Align pointer coords to the same space as `screenUv` before computing direction.
    const pointerUv = vec2(pointerUvNdc01.x, pointerUvNdc01.y.oneMinus())
    const resolution = this.watercolorBrush.uniforms.resolution

    // Direction from pointer -> fragment (external direction) in pixel-space,
    // so it stays circular regardless of aspect ratio.
    const dirFromPointerPx = screenUv
      .mul(resolution)
      .sub(pointerUv.mul(resolution))
    const dirFromPointerPxN = dirFromPointerPx.div(
      dirFromPointerPx.length().max(1e-5),
    )

    const displacementStrengthPx = 1.5
    const displacementMaxPx = 1.0
    const displacementPx = watercolorBrushValue
      .mul(displacementStrengthPx)
      .min(displacementMaxPx)
      .mul(displacementEnabled)
    const displacementUv = dirFromPointerPxN
      .mul(displacementPx)
      .mul(invResolution)

    // Note: sampling at (uv - offset) moves pixels along +offset.
    const displacedScene = sceneTexture.sample(screenUv.sub(displacementUv))
    const sceneAlpha = displacedScene.a
    const brightenAmount = watercolorBrushValue
      .mul(sceneAlpha)
      .mul(0.15)
      .mul(displacementEnabled)

    const sceneRgbBrightened = blendBrightenAlpha(
      displacedScene.rgb,
      watercolorTexture.rgb,
      brightenAmount,
    )

    // Preserve the scene alpha exactly.
    this.postProcessing.outputNode = vec4(sceneRgbBrightened, sceneAlpha)
  }

  update(_params: { delta: number }, fbo: THREE.RenderTarget) {
    if (!this.postProcessing) {
      return
    }

    if (this.texts.length) {
      this.texts.forEach((text) => {
        text.update()
      })
    }

    if (this.smallButtons.length) {
      this.smallButtons.forEach((smallButton) => {
        smallButton.update()
      })
    }

    this.interactionManager.update()

    // Mouse
    // Lerp mouse coordinates
    this.currentMouseCoords.x = lerp(
      this.currentMouseCoords.x,
      this.pointer.ndcX,
      0.1,
    )
    this.currentMouseCoords.y = lerp(
      this.currentMouseCoords.y,
      this.pointer.ndcY,
      0.1,
    )
    // Update uniform with lerped values
    this.config.mouseCoords.value.x = this.currentMouseCoords.x
    this.config.mouseCoords.value.y = this.currentMouseCoords.y

    //
    //
    // START WATERCOLOR PIPELINE
    //
    //
    const shouldUpdateWatercolor = this.getShouldUpdateWatercolor()
    if (shouldUpdateWatercolor) {
      this.watercolorBrush.update()
      this.renderer.setRenderTarget(this.watercolorBrush.targetB)
      this.renderer.render(this.watercolorBrush.bufferScene, this.camera)

      // Swap targets
      const temp = this.watercolorBrush.targetA
      this.watercolorBrush.targetA = this.watercolorBrush.targetB
      this.watercolorBrush.targetB = temp

      this.watercolorBrush.setDiffuseTexture(
        this.watercolorBrush.targetA.texture,
      )
    }

    this.renderer.setRenderTarget(fbo)

    //
    // Post Processing
    this.postProcessing.render()
  }

  private getShouldUpdateWatercolor() {
    const now =
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
        ? performance.now()
        : Date.now()

    const isLastValid =
      Number.isFinite(this.lastWatercolorMouseCoords.x) &&
      Number.isFinite(this.lastWatercolorMouseCoords.y)
    if (!isLastValid) {
      this.lastWatercolorMouseCoords.x = this.currentMouseCoords.x
      this.lastWatercolorMouseCoords.y = this.currentMouseCoords.y
      this.lastWatercolorMovementAt = now
      return true
    }

    const epsilon = 1e-4
    const dx = Math.abs(
      this.currentMouseCoords.x - this.lastWatercolorMouseCoords.x,
    )
    const dy = Math.abs(
      this.currentMouseCoords.y - this.lastWatercolorMouseCoords.y,
    )
    const didMove = dx > epsilon || dy > epsilon

    if (didMove) {
      this.lastWatercolorMouseCoords.x = this.currentMouseCoords.x
      this.lastWatercolorMouseCoords.y = this.currentMouseCoords.y
      this.lastWatercolorMovementAt = now
      return true
    }

    const isMovementTimeValid = Number.isFinite(this.lastWatercolorMovementAt)
    if (!isMovementTimeValid) {
      this.lastWatercolorMovementAt = now
      return true
    }

    // Keep the feedback pipeline running briefly so the history can decay.
    return now - this.lastWatercolorMovementAt < this.watercolorStopDelayMs
  }

  resize() {
    const { width, height } = this.sizes

    this.syncDimensions()
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()

    /*
    if (this.watercolorBrush) {
      this.watercolorBrush.resize()

      // IMPORTANT: after RenderTarget.setSize(), ensure the feedback sampler
      // points at the current (post-resize) texture, even if we don't update watercolor this frame.
      this.watercolorBrush.setDiffuseTexture(
        this.watercolorBrush.targetA.texture,
      )
    }
    */

    // Resizing texts
    if (this.texts.length) {
      this.texts.forEach((el) => el.resize())
    }
    if (this.smallButtons.length) {
      this.smallButtons.forEach((el) => el.resize())
    }
  }

  setDebugFolder(debugFolder?: FolderApi) {
    this.debugFolder = debugFolder
    if (this.debugFolder) {
      this.texts.forEach((text) => {
        text.setDebugFolder(debugFolder)
      })
    }
  }

  private getIsFinePointerDevice(): boolean {
    if (typeof window === "undefined") return true

    const mql = window.matchMedia?.(
      "(any-hover: hover) and (any-pointer: fine)",
    )
    if (mql?.matches) return true

    return false
  }
}

interface TextSceneConfig {
  color: THREE.UniformNode<THREE.Color>
  mouseCoords: THREE.UniformNode<THREE.Vector2>
  emissiveIntensity: THREE.UniformNode<number>
}
